// Client-only brands that distinguish the two id-0 "uncommitted" log channels from a
// committed timeline log. The wire payload is always `ILog` (no Swift/generated change);
// the Data-layer stores brand a log on ingest so a shared renderer can tell a live
// "current" entry or an in-progress "provisional" auto-log apart from a committed log
// without inspecting its (shared) id 0. See `CurrentLog`/`ProvisionalLog` in `Store.ts`.
import type { ILog } from "./Generated/LogbookModel/Log"

// The singleton "now" entry held by the `CurrentLog` store (fed by `Log/Current`).
export type ICurrentLog = ILog & { readonly kind: "current" };

// An in-progress uncommitted auto-log (e.g. a course change) held, keyed by its
// `metadata.provisionalKey`, by the `ProvisionalLog` store (fed by `Log/Provisional`).
export type IProvisionalLog = ILog & { readonly kind: "provisional" };

// The three forms a shared log renderer may receive. A committed log is a plain,
// untagged `ILog`; `logKind` folds it back to `"committed"`.
export type IAnyLog = ILog | ICurrentLog | IProvisionalLog;

export type LogKind = "committed" | "current" | "provisional";

// A committed `ILog` that carries neither brand — the shape a committed-store write API
// should accept, so a branded current/provisional log can't be committed by accident.
export type ICommittedLog = ILog & { kind?: never };

// Brand a raw broadcast/fetched `ILog` as the current log. Returns a fresh object so the
// source (which a caller may still treat as a plain committed log) is never mutated.
export function brandCurrent(log: ILog): ICurrentLog {
  return { ...log, kind: "current" };
}

// Brand a raw broadcast/fetched `ILog` as a provisional log (see `brandCurrent`).
export function brandProvisional(log: ILog): IProvisionalLog {
  return { ...log, kind: "provisional" };
}

// The kind of any log form: the brand if present, else `"committed"` for a plain `ILog`.
export function logKind(log: IAnyLog): LogKind {
  return "kind" in log ? log.kind : "committed";
}
